import type { PropsWithChildren, ReactNode } from "react"
import { createContext, useContext } from "react"

import { cn } from "../lib/utils"

// ============================================================
// CONTEXT - Set once at app root, use everywhere
// ============================================================

interface AdminContextValue {
  isAdmin: boolean
}

const AdminContext = createContext<AdminContextValue | null>(null)

export interface AdminProviderProps extends PropsWithChildren {
  /** Whether the current user is an admin */
  isAdmin: boolean
}

/**
 * AdminProvider - Wrap your app root to enable AdminGate components throughout.
 *
 * @example
 * // In your app root (e.g., App.tsx or layout)
 * const { session } = useAuthSession()
 * const isAdmin = isAdminUserFE(session?.user)
 *
 * return (
 *   <AdminProvider isAdmin={isAdmin}>
 *     <App />
 *   </AdminProvider>
 * )
 */
export function AdminProvider({ isAdmin, children }: AdminProviderProps) {
  return <AdminContext.Provider value={{ isAdmin }}>{children}</AdminContext.Provider>
}

/**
 * Hook to access admin status from context
 */
export function useAdminContext() {
  const ctx = useContext(AdminContext)
  if (!ctx) {
    // Return false if no provider - gracefully degrade
    return { isAdmin: false }
  }
  return ctx
}

// ============================================================
// ADMIN GATE COMPONENT
// ============================================================

export interface AdminGateProps extends PropsWithChildren {
  /** Override context - explicitly set admin status (optional if using AdminProvider) */
  isAdmin?: boolean
  /** Hide the visual indicator (shows by default to help identify admin-gated content) */
  hideIndicator?: boolean
  /** Custom label text for the indicator badge */
  indicatorLabel?: string
  /** Render this instead when user is not admin (default: null) */
  fallback?: ReactNode
  /** Additional className for the wrapper */
  className?: string
}

/**
 * AdminGate - Conditionally renders content for admin users only.
 *
 * Uses AdminProvider context by default. Can override with explicit `isAdmin` prop.
 * Shows visual indicator (violet border + badge) by default - use `hideIndicator` to disable.
 *
 * @example
 * // With context (recommended) - just wrap, no props needed
 * <AdminGate>
 *   <SecretAdminPanel />
 * </AdminGate>
 *
 * @example
 * // Hide indicator for cleaner production UI
 * <AdminGate hideIndicator>
 *   <AdminOnlyButton />
 * </AdminGate>
 *
 * @example
 * // Override context with explicit prop
 * <AdminGate isAdmin={customCheck}>
 *   <SpecialContent />
 * </AdminGate>
 *
 * @example
 * // With fallback content for non-admins
 * <AdminGate fallback={<UpgradePrompt />}>
 *   <PremiumFeature />
 * </AdminGate>
 */
export function AdminGate({
  isAdmin: isAdminProp,
  hideIndicator = false,
  indicatorLabel = "Admin",
  fallback = null,
  className,
  children,
}: AdminGateProps) {
  const ctx = useAdminContext()

  // Use explicit prop if provided, otherwise use context
  const isAdmin = isAdminProp ?? ctx.isAdmin

  if (!isAdmin) {
    return <>{fallback}</>
  }

  // If indicator hidden, just render children
  if (hideIndicator) {
    return <>{children}</>
  }

  // With indicator - wrap in a visually distinct container
  return (
    <div
      className={cn("relative rounded-md", "ring-1 ring-violet-500/50 ring-offset-1 ring-offset-background", className)}
    >
      {/* Admin badge */}
      <span className="absolute -right-2 -top-2 z-10 rounded bg-violet-500 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white shadow-xs">
        {indicatorLabel}
      </span>
      {children}
    </div>
  )
}
